import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import Seo from "@/components/feature/Seo";
import { faqs } from "@/mocks/site";

const groups = [
  { name: "关于看料", keys: [0, 1] },
  { name: "内容与更新", keys: [2, 3] },
  { name: "使用与收费", keys: [4, 5] },
];

export default function FaqPage() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <PageLayout dark={false}>
      <Seo
        title="常见问题 - 关于看料精选、更新频率、投稿与收费"
        description="关于看料精选与热门推荐、内容更新频率、投稿方式、是否收费等常见问题解答，帮助读者更好地使用看料内容推荐平台。"
        keywords="看料,看料常见问题,看料精选,投稿,内容更新"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://01x0wyo.cn/" },
              { "@type": "ListItem", position: 2, name: "常见问题", item: "https://01x0wyo.cn/faq" },
            ],
          },
        ]}
      />

      <section className="bg-background-50 pt-24 md:pt-28">
        <div className="mx-auto max-w-7xl px-4 pb-6 md:px-6">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">常见问题</span>
          </nav>
          <div className="mx-auto mt-10 max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-md bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary-800">
              <i className="ri-question-answer-line" />
              常见问题 FAQ
            </span>
            <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-5xl">
              你想知道的，都在这里
            </h1>
            <p className="mt-4 text-base leading-8 text-foreground-600">
              关于看料精选、热门推荐、内容更新与投稿方式，我们整理了一些高频问题，
              希望能帮到你。没有找到答案？欢迎来信与我们交流。
            </p>
          </div>
        </div>
      </section>

      {/* 分组问答 */}
      <section className="bg-background-50">
        <div className="mx-auto max-w-4xl px-4 pb-16 md:px-6 md:pb-24">
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {groups.map((g) => (
              <span key={g.name} className="rounded-full bg-background-100 px-4 py-1.5 text-xs font-medium text-foreground-600">
                {g.name} · {g.keys.length} 条
              </span>
            ))}
          </div>

          <div className="mt-10 space-y-4">
            {faqs.map((item, idx) => {
              const on = open === idx;
              return (
                <div
                  key={item.q}
                  className={`overflow-hidden rounded-xl border bg-background-100 transition-colors ${
                    on ? "border-primary-300 bg-background-50" : "border-background-200/80"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpen(on ? null : idx)}
                    aria-expanded={on}
                    className="flex w-full items-center gap-4 px-5 py-5 text-left"
                  >
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-heading text-sm font-bold ${on ? "bg-primary-500 text-background-50" : "bg-secondary-500 text-background-50"}`}>
                      Q
                    </span>
                    <span className="flex-1 font-heading text-base font-bold text-foreground-950 md:text-lg">
                      {item.q}
                    </span>
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-all ${on ? "rotate-180 bg-primary-500 text-background-50" : "bg-background-200/70 text-foreground-600"}`}>
                      <i className="ri-arrow-down-s-line text-lg" />
                    </span>
                  </button>
                  <div className={`grid transition-all duration-300 ${on ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden">
                      <div className="px-5 pb-6 pl-[4.5rem]">
                        <span className="mb-2 block font-heading text-sm font-bold text-primary-600">答：</span>
                        <p className="text-sm leading-7 text-foreground-700 md:text-[15px] md:leading-8">{item.a}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 还没解决 */}
          <div className="mt-12 flex flex-col items-center gap-4 rounded-2xl bg-accent-50/60 border border-accent-100 px-6 py-8 text-center md:flex-row md:justify-between md:text-left">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950">还有疑问没解决？</h2>
              <p className="mt-2 text-sm text-foreground-600">
                关于看料的任何问题，欢迎发送邮件，我们会尽快回复你。
              </p>
            </div>
            <a
              href="mailto:hello@kanliao.example"
              className="inline-flex h-11 shrink-0 items-center gap-2 rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-mail-line" /> 联系编辑团队
            </a>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}